import React, { createContext, useCallback, useContext, useEffect } from 'react';
import PointerReplace from '@polestar/component-warehouse-react/components/pointer-replace/PointerReplace';
import { motionValue, useMotionValue } from 'framer-motion';
import useMutableValue, { mutableValue, } from '@polestar/component-warehouse-react/hooks/useMutableValue';
const noProviderFunction = (handleName) => () => {
    // eslint-disable-next-line no-console
    console.warn(`Trying to use pointer context consumer, but no context provider for "${handleName}". Please make sure you use the usePointerReplace inside a <PolestarApp /> or check if there are multiple @polestar/component-warehouse-react versions installed due to (for example) shared modules hard dependencies.`);
};
/**
 * @internal
 */
export const PointerReplaceContext = createContext({
    counter: mutableValue(0),
    positionX: motionValue(0),
    positionY: motionValue(0),
    iconOffsetX: motionValue(0),
    iconOffsetY: motionValue(0),
    opacity: motionValue(1),
    setIcon: noProviderFunction('setIcon'),
    setText: noProviderFunction('setText'),
    setEmpty: noProviderFunction('setEmpty'),
    hide: noProviderFunction('hide'),
});
export const usePointerReplace = () => {
    const { hide, counter, ...pointerContext } = useContext(PointerReplaceContext);
    useEffect(() => {
        counter.set(counter.get() + 1);
        return () => {
            counter.set(counter.get() - 1);
            hide();
        };
    }, [counter, hide]);
    return { hide, ...pointerContext };
};
/**
 * @internal
 */
export var PointerContentTypes;
(function (PointerContentTypes) {
    PointerContentTypes["empty"] = "empty";
    PointerContentTypes["text"] = "text";
    PointerContentTypes["icon"] = "icon";
})(PointerContentTypes || (PointerContentTypes = {}));
/**
 * @internal
 */
export const PointerReplaceProvider = ({ children }) => {
    const counter = useMutableValue(0);
    const content = useMutableValue(null);
    const mouseDown = useMutableValue(false);
    const positionX = useMotionValue(0);
    const positionY = useMotionValue(0);
    const iconOffsetX = useMotionValue(0);
    const iconOffsetY = useMotionValue(0);
    const opacity = useMotionValue(1);
    useEffect(() => {
        const handleMouseMove = (e) => {
            if (counter.get() > 0) {
                positionX.set(e.clientX);
                positionY.set(e.clientY);
            }
        };
        const handleMouseDown = () => {
            if (counter.get() > 0) {
                mouseDown.set(true);
            }
        };
        const handleMouseUp = () => {
            mouseDown.set(false);
        };
        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mousedown', handleMouseDown);
        window.addEventListener('mouseup', handleMouseUp);
        return () => {
            document.documentElement.style.cursor = '';
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mousedown', handleMouseDown);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [content, counter, mouseDown, positionX, positionY]);
    useEffect(() => {
        document.documentElement.style.cursor = !!content.get() ? 'none' : '';
        return content.on('change', (latestContent) => {
            document.documentElement.style.cursor = !!latestContent ? 'none' : '';
        });
    }, [content]);
    const handleSetEmpty = useCallback((theme) => {
        content.set({
            type: PointerContentTypes.empty,
            theme,
        });
    }, [content]);
    const handleSetIcon = useCallback((icon, theme) => {
        content.set({
            type: PointerContentTypes.icon,
            content: icon,
            theme,
        });
    }, [content]);
    const handleSetText = useCallback((text, theme) => {
        content.set({
            type: PointerContentTypes.text,
            content: text,
            theme,
        });
    }, [content]);
    const handleHide = useCallback(() => {
        content.set(null);
    }, [content]);
    return (React.createElement(PointerReplaceContext.Provider, { value: {
            counter,
            positionX,
            positionY,
            setIcon: handleSetIcon,
            setText: handleSetText,
            setEmpty: handleSetEmpty,
            hide: handleHide,
            opacity,
            iconOffsetX,
            iconOffsetY,
        } },
        children,
        React.createElement(PointerReplace, { content: content, mouseDown: mouseDown, positionX: positionX, positionY: positionY, opacity: opacity, iconOffsetX: iconOffsetX, iconOffsetY: iconOffsetY })));
};
